import React, { createContext, useContext, useEffect, useState } from "react";
import { AppStateFeature } from "../../domain/appState/appStateFeature";
import { InitialDataFeature } from "../../domain/initialData/initialDataFeature";
import { RootStateNavigator } from "../../domain/appState/models/RootStateNavigator";

interface AppStateContextValue {
  rootState: RootStateNavigator;
}

const AppStateContext = createContext<AppStateContextValue>({
  rootState: RootStateNavigator.splash,
});

export const useAppState = (): AppStateContextValue => {
  return useContext(AppStateContext);
};

interface AppStateProviderProps {
  children: React.ReactNode;
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({
  children,
}) => {
  const [rootState, setRootState] = useState<RootStateNavigator>(
    RootStateNavigator.splash,
  );

  // Observe app state changes
  useEffect(() => {
    const appStateFeature = AppStateFeature.getInstance();
    const unsubscribe = appStateFeature.observeAppStateFeature(
      (newRootState) => {
        setRootState(newRootState);
      },
    );

    return () => unsubscribe();
  }, []);

  // Load initial data and signal readiness (replaces RN SplashScreen logic)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await InitialDataFeature.fetchAll();
      } catch (error) {
        console.error("Error loading initial data:", error);
        // Continue even if data fetch fails
      } finally {
        AppStateFeature.getInstance().setInitialDataFinishedLoading(true);
      }
    };

    loadInitialData();
  }, []);

  return (
    <AppStateContext.Provider value={{ rootState }}>
      {children}
    </AppStateContext.Provider>
  );
};
